// Generate an Adobe Lightroom / Camera Raw *develop preset* (.xmp) from an
// in-app grade. This is the "creative look" as native Lightroom sliders, meant
// to be dropped on top of a clip you've already exposure/white-balance
// calibrated — so we deliberately DO NOT write Exposure or White Balance tags
// (the preset leaves them untouched). Slider math between our engine and Adobe's
// isn't identical, so this is an excellent starting point you fine-tune per frame
// rather than a byte-exact match (for that, use the look .cube).

const round = (v) => Math.round(Number(v) || 0);
const clampi = (v, lo, hi) => Math.max(lo, Math.min(hi, round(v)));

// Our HSL band order matches Lightroom's eight color bands 1:1.
const BANDS = ['Red', 'Orange', 'Yellow', 'Green', 'Aqua', 'Blue', 'Purple', 'Magenta'];

import SITE from '../site';

export function lightroomPresetXmp(grade, name = SITE.lookName, group = SITE.lookGroup) {
  const g = grade || {};
  const basic = g.basic || {};
  const pres = g.presence || {};
  const hsl = g.hsl || [];
  const cg = g.colorGrading || {};
  const fx = g.effects || {};

  const attrs = [];
  const A = (k, v) => attrs.push(`   crs:${k}="${v}"`);

  A('Version', '15.5');
  A('ProcessVersion', '11.0');

  // Tone (exposure intentionally omitted — set per clip).
  A('Contrast2012', clampi(basic.contrast, -100, 100));
  A('Highlights2012', clampi(basic.highlights, -100, 100));
  A('Shadows2012', clampi(basic.shadows, -100, 100));
  A('Whites2012', clampi(basic.whites, -100, 100));
  A('Blacks2012', clampi(basic.blacks, -100, 100));

  // Presence.
  A('Texture', clampi(pres.texture, -100, 100));
  A('Clarity2012', clampi(pres.clarity, -100, 100));
  A('Dehaze', clampi(pres.dehaze, -100, 100));
  A('Vibrance', clampi(pres.vibrance, -100, 100));
  A('Saturation', clampi(pres.saturation, -100, 100));

  // HSL / Color Mixer.
  BANDS.forEach((band, i) => {
    const b = hsl[i] || {};
    A(`HueAdjustment${band}`, clampi(b.h, -100, 100));
    A(`SaturationAdjustment${band}`, clampi(b.s, -100, 100));
    A(`LuminanceAdjustment${band}`, clampi(b.l, -100, 100));
  });

  // Color Grading (modern wheels).
  const wheel = (key, region) => {
    const w = cg[region] || {};
    A(`ColorGrade${key}Hue`, clampi(w.h, 0, 360));
    A(`ColorGrade${key}Sat`, clampi(w.s, 0, 100));
    A(`ColorGrade${key}Lum`, clampi(w.l, -100, 100));
  };
  wheel('Shadow', 'shadows');
  wheel('Midtone', 'midtones');
  wheel('Highlight', 'highlights');
  wheel('Global', 'global');
  A('ColorGradeBlending', clampi(cg.blending ?? 50, 0, 100));

  // Effects.
  if (fx.vignette) A('PostCropVignetteAmount', clampi(-Math.abs(fx.vignette), -100, 0));
  if (fx.grain) A('GrainAmount', clampi(fx.grain, 0, 100));

  A('HasSettings', 'True');

  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  return `<x:xmpmeta xmlns:x="adobe:ns:meta/" x:xmptk="${esc(SITE.siteName)}">
 <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
  <rdf:Description rdf:about=""
   xmlns:crs="http://ns.adobe.com/camera-raw-settings/1.0/"
${attrs.join('\n')}>
   <crs:Name>
    <rdf:Alt>
     <rdf:li xml:lang="x-default">${esc(name)}</rdf:li>
    </rdf:Alt>
   </crs:Name>
   <crs:Group>
    <rdf:Alt>
     <rdf:li xml:lang="x-default">${esc(group)}</rdf:li>
    </rdf:Alt>
   </crs:Group>
  </rdf:Description>
 </rdf:RDF>
</x:xmpmeta>`;
}
